import {
  ServerConnection,
  SessionManager,
  ContentsManager,
  KernelManager,
  TerminalManager,
  Session,
  Kernel,
  KernelMessage,
} from '@jupyterlab/services';

import { getApiUrl } from '../utils/api';

export interface NotebookConfig {
  sessionId: string;
  token?: string;
  baseUrl?: string;
}

export interface ExecutionResult {
  outputs: any[];
  error?: string;
}

class NotebookService {
  private serverSettings: ServerConnection.ISettings;
  private sessionManager: SessionManager;
  public contentsManager: ContentsManager;
  private kernelManager: KernelManager;
  private terminalManager: TerminalManager;
  private currentSession: Session.ISessionConnection | null = null;
  private sessionId: string;
  private _isDisposed: boolean = false;
  private _initialized: boolean = false;

  constructor(config: NotebookConfig) {
    this.sessionId = config.sessionId;
    
    // configure connection to jupyter server via our backend proxy
    // Use the v1 proxy endpoint
    // IMPORTANT: Must use full backend URL, not relative path
    const backendHost = getApiUrl();
    const baseUrl = config.baseUrl || `${backendHost}/api/v1/notebooks/proxy/${config.sessionId}/`;
    
    // Token is required - the proxy handles authentication
    if (!config.token) {
      throw new Error('Jupyter session token is required');
    }
    
    // Create custom fetch that adds our session token to all requests
    const originalFetch = window.fetch;
    const sessionToken = config.token; // Capture token in closure
    const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      // Add token to URL if it's a request to our proxy (v1 or v2)
      if (typeof input === 'string' && (input.includes('/api/v1/notebooks/proxy/') || input.includes('/api/v2/notebooks/proxy/')) && sessionToken) {
        const url = new URL(input, window.location.origin);
        url.searchParams.set('token', sessionToken);
        input = url.toString();
      }
      
      // Also add tokens as headers
      const headers = new Headers(init?.headers || {});
      if (sessionToken) {
        headers.set('X-Session-Token', sessionToken);
      }
      
      // Add auth token for API authentication
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      
      const response = await originalFetch(input, {
        ...init,
        headers,
        credentials: 'include'
      });
      
      // Check for 401 and trigger reconnection
      if (response.status === 401 && typeof input === 'string' && (input.includes('/api/v1/notebooks/proxy/') || input.includes('/api/v2/notebooks/proxy/'))) {
        console.error('Session token expired or invalid. Please refresh the page to reconnect.');
        // Clear the invalid token from storage
        const sessionId = config.sessionId;
        if (sessionId) {
          sessionStorage.removeItem(`notebook_token_${sessionId}`);
        }
      }
      
      return response;
    };
    
    // For WebSockets, we need to use the base URL without trailing slash
    const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://').replace(/\/$/, '');
    
    this.serverSettings = ServerConnection.makeSettings({
      baseUrl,
      wsUrl,
      token: config.token,
      appendToken: true,  // This will add the token as a query parameter
      fetch: customFetch as any  // Use our custom fetch
    });
    
    // NotebookService initialized

    // initialize kernel manager first (required by session manager)
    this.kernelManager = new KernelManager({ serverSettings: this.serverSettings });
    this.contentsManager = new ContentsManager({ serverSettings: this.serverSettings });
    
    // initialize session manager with kernel manager
    this.sessionManager = new SessionManager({ 
      serverSettings: this.serverSettings,
      kernelManager: this.kernelManager 
    });
    
    // initialize terminal manager
    this.terminalManager = new TerminalManager({ serverSettings: this.serverSettings });
  }

  async initialize(): Promise<void> {
    if (this._initialized) return;

    // wait for managers to be ready
    await Promise.all([
      this.sessionManager.ready,
      this.kernelManager.ready,
    ]);
    
    // Try to clear any stale sessions to prevent connection to old kernels
    // This is wrapped in try-catch to ensure it doesn't break initialization
    try {
      // Refresh the list of running sessions
      await this.sessionManager.refreshRunning();
      
      // Get iterator of running sessions and convert to array
      const runningSessionsIterator = this.sessionManager.running();
      const runningSessions = Array.from(runningSessionsIterator);
      const sessionsToShutdown = [];
      
      // Collect sessions that match this notebook
      for (const session of runningSessions) {
        // Check if this session belongs to our current notebook
        if (session.name === this.sessionId || session.path === this.sessionId) {
          sessionsToShutdown.push(session);
        }
      }
      
      // Shutdown stale sessions
      for (const session of sessionsToShutdown) {
        console.log(`Cleaning up stale session: ${session.id} for notebook: ${this.sessionId}`);
        try {
          await this.sessionManager.shutdown(session.id);
        } catch (shutdownError) {
          // Session might already be gone, that's okay
          console.log('Session already shut down or not found:', shutdownError);
        }
      }
      
      if (sessionsToShutdown.length > 0) {
        console.log(`Cleaned up ${sessionsToShutdown.length} stale session(s)`);
      }
    } catch (e) {
      // If we can't refresh sessions, Jupyter may not be fully ready yet.
      // Don't mark as initialized — caller should retry.
      console.warn('Could not refresh running sessions — Jupyter may still be starting:', e);
      throw e;
    }

    this._initialized = true;
  }

  resetInitialization(): void {
    this._initialized = false;
  }

  // session management
  async startSession(path: string = 'Untitled.ipynb'): Promise<Session.ISessionConnection> {
    try {
      // Starting Jupyter session
      
      this.currentSession = await this.sessionManager.startNew({
        name: path,
        path,
        type: 'notebook',
        kernel: { name: 'python3' },
      });
      
      return this.currentSession;
    } catch (error) {
      console.error('Failed to start Jupyter session:', error);
      console.error('Session manager state:', {
        isReady: this.sessionManager.isReady,
        serverSettings: this.serverSettings
      });
      throw error;
    }
  }

  async stopSession(): Promise<void> {
    if (this.currentSession) {
      try {
        // Check if session is already disposed before trying to shutdown
        if (!this.currentSession.isDisposed) {
          await this.currentSession.shutdown();
        }
      } catch (e) {
        // Session might already be disposed, that's okay
        console.log('Session already disposed or error during shutdown:', e);
      }
      this.currentSession = null;
    }
  }

  // kernel operations
  async executeCode(code: string, onOutput?: (output: any) => void): Promise<ExecutionResult> {
    if (!this.currentSession?.kernel) {
      throw new Error('No active kernel. Start a session first.');
    }

    const outputs: any[] = [];
    const kernel = this.currentSession.kernel;

    return new Promise((resolve, reject) => {
      const future = kernel.requestExecute({ code });

      // collect outputs from iopub channel — stream each to caller immediately
      future.onIOPub = (msg: KernelMessage.IIOPubMessage) => {
        const msgType = msg.header.msg_type;
        let parsed: any = null;

        switch (msgType) {
          case 'stream': {
            const streamMsg = msg as KernelMessage.IStreamMsg;
            parsed = {
              type: 'stream',
              name: streamMsg.content.name,
              text: streamMsg.content.text,
            };
            break;
          }

          case 'display_data':
          case 'execute_result': {
            const displayMsg = msg as KernelMessage.IDisplayDataMsg;
            parsed = {
              type: msgType,
              data: displayMsg.content.data,
              metadata: displayMsg.content.metadata,
            };
            break;
          }

          case 'error': {
            const errorMsg = msg as KernelMessage.IErrorMsg;
            parsed = {
              type: 'error',
              ename: errorMsg.content.ename,
              evalue: errorMsg.content.evalue,
              traceback: errorMsg.content.traceback,
            };
            break;
          }

          case 'status':
            break;
        }

        if (parsed) {
          outputs.push(parsed);
          onOutput?.(parsed);
        }
      };

      // handle completion
      future.done.then(() => {
        resolve({ outputs });
      }).catch((error) => {
        reject(error);
      });
    });
  }

  async requestComplete(code: string, cursorPos: number): Promise<{
    matches: string[];
    cursor_start: number;
    cursor_end: number;
  }> {
    if (!this.currentSession?.kernel) {
      throw new Error('No active kernel');
    }
    const reply = await this.currentSession.kernel.requestComplete({ code, cursor_pos: cursorPos });
    const content = reply.content as any;
    return {
      matches: content.matches || [],
      cursor_start: content.cursor_start ?? cursorPos,
      cursor_end: content.cursor_end ?? cursorPos,
    };
  }

  async requestInspect(code: string, cursorPos: number, detailLevel: 0 | 1 = 0): Promise<{
    found: boolean;
    data: Record<string, string>;
  }> {
    if (!this.currentSession?.kernel) {
      throw new Error('No active kernel');
    }
    const reply = await this.currentSession.kernel.requestInspect({
      code,
      cursor_pos: cursorPos,
      detail_level: detailLevel,
    });
    const content = reply.content as any;
    return {
      found: content.found || false,
      data: content.data || {},
    };
  }

  /**
   * Silently execute a Python snippet that introspects kernel variables.
   * Returns a list of variable descriptors without affecting execution count.
   */
  async getVariables(): Promise<Array<{
    name: string;
    type: string;
    value: string;
    shape?: string;
    size?: number;
    len?: number;
    category: string;
    preview?: string;
  }>> {
    if (!this.currentSession?.kernel) {
      return [];
    }

    const inspectCode = `
import sys as __sys__, json as __json__
__vars__ = []
for __vname__, __val__ in sorted(globals().items()):
    if __vname__.startswith('_') or __vname__ in ('In','Out','get_ipython','exit','quit') or (callable(__val__) and type(__val__).__module__ == 'builtins'):
        continue
    __tp__ = type(__val__).__name__
    __info__ = {'name': __vname__, 'type': __tp__}
    if __tp__ in ('DataFrame','Series','GeoDataFrame'):
        __info__['category'] = 'dataframe'
    elif __tp__ in ('ndarray','Tensor','tensor','DeviceArray'):
        __info__['category'] = 'array'
    elif __tp__ in ('list','dict','tuple','set','frozenset','deque'):
        __info__['category'] = 'collection'
    elif __tp__ in ('int','float','complex','str','bool','NoneType','Decimal','Fraction'):
        __info__['category'] = 'scalar'
    elif callable(__val__):
        __info__['category'] = 'function'
    else:
        __info__['category'] = 'other'
    try:
        try:
            __info__['size'] = __sys__.getsizeof(__val__)
        except (TypeError, AttributeError):
            pass
        if hasattr(__val__, 'shape') and not callable(__val__.shape):
            __info__['shape'] = str(__val__.shape)
        if hasattr(__val__, '__len__') and not isinstance(__val__, str):
            __info__['len'] = len(__val__)
        __r__ = repr(__val__)
        __info__['value'] = __r__[:300] + ('...' if len(__r__) > 300 else '')
        if __tp__ in ('DataFrame','GeoDataFrame'):
            try:
                __info__['preview'] = __val__.head(3).to_string()
            except Exception:
                pass
        elif __tp__ == 'Series':
            try:
                __info__['preview'] = __val__.head(5).to_string()
            except Exception:
                pass
        elif __tp__ == 'ndarray':
            try:
                __flat__ = __val__.flatten()[:5]
                __info__['preview'] = 'dtype=' + str(__val__.dtype) + ', [' + ', '.join(str(x) for x in __flat__) + ('...' if __val__.size > 5 else '') + ']'
                del __flat__
            except Exception:
                pass
    except Exception:
        __info__['value'] = '<error>'
    __vars__.append(__info__)
print(__json__.dumps(__vars__))
del __vars__, __vname__, __val__, __info__, __json__, __sys__, __tp__
try:
    del __r__
except NameError:
    pass
`;

    return new Promise((resolve) => {
      const kernel = this.currentSession!.kernel!;
      const future = kernel.requestExecute({ code: inspectCode, silent: true, store_history: false });
      let jsonOutput = '';

      future.onIOPub = (msg: KernelMessage.IIOPubMessage) => {
        if (msg.header.msg_type === 'stream') {
          const streamMsg = msg as KernelMessage.IStreamMsg;
          if (streamMsg.content.name === 'stdout') {
            jsonOutput += streamMsg.content.text;
          }
        }
      };

      future.done.then(() => {
        try {
          resolve(JSON.parse(jsonOutput));
        } catch {
          console.warn('[VariableInspector] Failed to parse introspection output:', jsonOutput.slice(0, 500));
          resolve([]);
        }
      }).catch(() => resolve([]));
    });
  }

  /**
   * Set the kernel's working directory to match the notebook's containing directory.
   * Mirrors classic Jupyter Notebook behavior where os.listdir('.') shows sibling files.
   */
  async setCwd(notebookPath: string): Promise<void> {
    if (!this.currentSession?.kernel) return;

    const lastSlash = notebookPath.lastIndexOf('/');
    if (lastSlash <= 0) return; // root-level notebook — CWD is already workspace root

    const dirPath = notebookPath.substring(0, lastSlash);
    // Jupyter root_dir maps to workspace/, so Contents API paths are relative to it
    const code = `import os as __os__; __os__.chdir(__os__.path.join(__os__.environ.get('JUPYTER_HOME', __os__.environ.get('HOME', '/home/propalgos')), 'workspace', ${JSON.stringify(dirPath)})); del __os__`;

    const kernel = this.currentSession.kernel;
    const future = kernel.requestExecute({ code, silent: true, store_history: false });
    await future.done;
  }

  async interruptKernel(): Promise<void> {
    if (!this.currentSession?.kernel) {
      throw new Error('No active kernel');
    }
    await this.currentSession.kernel.interrupt();
  }

  async restartKernel(): Promise<void> {
    if (!this.currentSession?.kernel) {
      throw new Error('No active kernel');
    }
    await this.currentSession.kernel.restart();
  }

  getKernelStatus(): Kernel.Status | null {
    return this.currentSession?.kernel?.status || null;
  }

  // file operations
  async listFiles(path: string = ''): Promise<any> {
    try {
      const content = await this.contentsManager.get(path, { content: true });
      return content;
    } catch (error) {
      console.error('Failed to list files:', error);
      throw error;
    }
  }

  async saveNotebook(path: string, content: any): Promise<void> {
    try {
      await this.contentsManager.save(path, {
        type: 'notebook',
        format: 'json',
        content,
      });
    } catch (error) {
      console.error('Failed to save notebook:', error);
      throw error;
    }
  }

  async loadNotebook(path: string): Promise<any> {
    try {
      const model = await this.contentsManager.get(path, { content: true });
      return model.content;
    } catch (error) {
      console.error('Failed to load notebook:', error);
      throw error;
    }
  }

  async createNotebook(path: string): Promise<any> {
    try {
      const model = await this.contentsManager.save(path, {
        type: 'notebook',
        format: 'json',
        content: {
          cells: [],
          metadata: {
            kernelspec: { display_name: 'Python 3', language: 'python', name: 'python3' },
            language_info: { name: 'python' },
          },
          nbformat: 4,
          nbformat_minor: 5,
        },
      });
      return model;
    } catch (error) {
      console.error('Failed to create notebook:', error);
      throw error;
    }
  }

  async createFile(path: string): Promise<any> {
    try {
      const model = await this.contentsManager.save(path, {
        type: 'file',
        format: 'text',
        content: '',
      });
      return model;
    } catch (error) {
      console.error('Failed to create file:', error);
      throw error;
    }
  }

  async createDirectory(path: string): Promise<any> {
    try {
      const model = await this.contentsManager.save(path, {
        type: 'directory',
      });
      return model;
    } catch (error) {
      console.error('Failed to create directory:', error);
      throw error;
    }
  }

  async deleteFile(path: string): Promise<void> {
    try {
      await this.contentsManager.delete(path);
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  async renameFile(oldPath: string, newPath: string): Promise<any> {
    try {
      return await this.contentsManager.rename(oldPath, newPath);
    } catch (error) {
      console.error('Failed to rename file:', error);
      throw error;
    }
  }

  async duplicateFile(path: string): Promise<any> {
    try {
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      return await this.contentsManager.copy(path, dir);
    } catch (error) {
      console.error('Failed to duplicate file:', error);
      throw error;
    }
  }

  async copyFileTo(srcPath: string, destDir: string): Promise<any> {
    try {
      return await this.contentsManager.copy(srcPath, destDir);
    } catch (error) {
      console.error('Failed to copy file:', error);
      throw error;
    }
  }

  /**
   * Copy an entire directory (non-recursive for flat project dirs like crypto_rsi_scanner).
   * Creates the destination subdirectory, then copies each non-hidden, non-directory file
   * sequentially. Returns partial-failure info so callers can degrade gracefully.
   */
  async copyDirectoryTo(srcDir: string, destParent: string): Promise<{
    destDir: string;
    copied: string[];
    failed: Array<{ name: string; error: string }>;
  }> {
    const dirName = srcDir.split('/').pop() || srcDir;
    const destDirPath = `${destParent}/${dirName}`;

    // Create destination directory (ignore "already exists" errors)
    try {
      await this.createDirectory(destDirPath);
    } catch {
      // Directory may already exist — safe to continue
    }

    // List source directory contents
    const listing = await this.contentsManager.get(srcDir, { content: true });
    const items: Array<{ name: string; path: string; type: string }> = listing.content || [];

    const copied: string[] = [];
    const failed: Array<{ name: string; error: string }> = [];

    // Copy each non-hidden, non-directory file sequentially
    for (const item of items) {
      if (item.type === 'directory') continue;
      if (item.name.startsWith('.')) continue;

      try {
        await this.contentsManager.copy(item.path, destDirPath);
        copied.push(item.name);
      } catch (err: any) {
        console.warn(`[copyDirectoryTo] Failed to copy ${item.name}:`, err);
        failed.push({ name: item.name, error: err?.message || String(err) });
      }
    }

    return { destDir: destDirPath, copied, failed };
  }

  /**
   * Check if a path exists without throwing.
   */
  async fileExists(path: string): Promise<boolean> {
    try {
      await this.contentsManager.get(path, { content: false });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get raw notebook model with content for hashing.
   */
  async getNotebookModel(path: string): Promise<any | null> {
    try {
      const model = await this.contentsManager.get(path, { content: true });
      return model;
    } catch {
      return null;
    }
  }

  /**
   * Write content to an exact destination path (no auto-suffix like copy() does).
   * Uses save() which overwrites or creates at the exact path specified.
   */
  async saveContentTo(sourceModel: any, destPath: string): Promise<any> {
    return await this.contentsManager.save(destPath, {
      type: sourceModel.type,
      format: sourceModel.format,
      content: sourceModel.content,
    });
  }

  /**
   * Smart copy: checks if dest already exists and compares content hashes.
   * Returns the action taken and the destination path.
   */
  async smartCopyFileTo(srcPath: string, destDir: string): Promise<{
    destPath: string;
    action: 'copy-fresh' | 'open-existing' | 'open-modified';
  }> {
    const { decideCopyAction, hashNotebook, deriveDestPath } = await import('./SampleCopyResolver');

    const destPath = deriveDestPath(srcPath, destDir);
    const destExists = await this.fileExists(destPath);

    if (!destExists) {
      const sourceModel = await this.getNotebookModel(srcPath);
      if (!sourceModel) throw new Error(`Source not found: ${srcPath}`);
      await this.saveContentTo(sourceModel, destPath);
      return { destPath, action: 'copy-fresh' };
    }

    // Dest exists — hash both in parallel
    const [sourceModel, destModel] = await Promise.all([
      this.getNotebookModel(srcPath),
      this.getNotebookModel(destPath),
    ]);

    const [sourceHash, destHash] = await Promise.all([
      sourceModel?.content ? hashNotebook(sourceModel.content) : null,
      destModel?.content ? hashNotebook(destModel.content) : null,
    ]);

    const decision = decideCopyAction(srcPath, destPath, {
      sourceExists: !!sourceModel,
      destExists: true,
      sourceHash,
      destHash,
    });

    if (decision.action === 'copy-fresh') {
      const model = sourceModel || await this.getNotebookModel(srcPath);
      if (model) await this.saveContentTo(model, destPath);
    }

    return { destPath: decision.destPath, action: decision.action };
  }

  /**
   * Smart directory copy: per-file dedup within a directory.
   * Creates dest dir if needed, then smart-copies each file.
   */
  async smartCopyDirectoryTo(srcDir: string, destParent: string): Promise<{
    destDir: string;
    copied: string[];
    skipped: string[];
    failed: Array<{ name: string; error: string }>;
  }> {
    const { decideCopyAction, hashNotebook } = await import('./SampleCopyResolver');

    const dirName = srcDir.split('/').pop() || srcDir;
    const destDirPath = srcDir.startsWith('samples/strategy_templates')
      ? destParent
      : `${destParent}/${dirName}`;

    if (destDirPath !== destParent) {
      try {
        await this.createDirectory(destDirPath);
      } catch {
        // Directory may already exist
      }
    }

    const listing = await this.contentsManager.get(srcDir, { content: true });
    const items: Array<{ name: string; path: string; type: string }> = listing.content || [];

    const copied: string[] = [];
    const skipped: string[] = [];
    const failed: Array<{ name: string; error: string }> = [];

    for (const item of items) {
      if (item.type === 'directory') continue;
      if (item.name.startsWith('.')) continue;

      const destFilePath = `${destDirPath}/${item.name}`;

      try {
        const destExists = await this.fileExists(destFilePath);

        if (!destExists) {
          const sourceModel = await this.getNotebookModel(item.path);
          if (sourceModel) {
            await this.saveContentTo(sourceModel, destFilePath);
            copied.push(item.name);
          }
        } else if (item.name.endsWith('.ipynb')) {
          const [sourceModel, destModel] = await Promise.all([
            this.getNotebookModel(item.path),
            this.getNotebookModel(destFilePath),
          ]);

          const [sourceHash, destHash] = await Promise.all([
            sourceModel?.content ? hashNotebook(sourceModel.content) : null,
            destModel?.content ? hashNotebook(destModel.content) : null,
          ]);

          const decision = decideCopyAction(item.path, destFilePath, {
            sourceExists: !!sourceModel,
            destExists: true,
            sourceHash,
            destHash,
          });

          if (decision.action === 'copy-fresh') {
            const model = sourceModel || await this.getNotebookModel(item.path);
            if (model) await this.saveContentTo(model, destFilePath);
            copied.push(item.name);
          } else {
            skipped.push(item.name);
          }
        } else {
          // Non-notebook file exists — skip
          skipped.push(item.name);
        }
      } catch (err: any) {
        failed.push({ name: item.name, error: err?.message || String(err) });
      }
    }

    return { destDir: destDirPath, copied, skipped, failed };
  }

  async getFileContent(path: string): Promise<any> {
    try {
      return await this.contentsManager.get(path, { content: true });
    } catch (error) {
      console.error('Failed to get file content:', error);
      throw error;
    }
  }

  async loadTextFile(path: string): Promise<string> {
    const model = await this.contentsManager.get(path, { content: true, type: 'file', format: 'text' });
    if (typeof model.content !== 'string') {
      throw new Error(`Expected text content for ${path}, got ${typeof model.content}`);
    }
    return model.content;
  }

  async saveTextFile(path: string, content: string): Promise<void> {
    await this.contentsManager.save(path, {
      type: 'file',
      format: 'text',
      content,
    });
  }

  // terminal operations
  async startTerminal(): Promise<any> {
    try {
      const terminal = await this.terminalManager.startNew();
      return terminal;
    } catch (error) {
      console.error('Failed to start terminal:', error);
      throw error;
    }
  }

  // Check if service is disposed
  get isDisposed(): boolean {
    return this._isDisposed;
  }

  // cleanup
  async dispose(): Promise<void> {
    // Prevent double-disposal
    if (this._isDisposed) {
      console.log('NotebookService already disposed, skipping');
      return;
    }
    this._isDisposed = true;

    // Stop the current session first
    if (this.currentSession) {
      try {
        await this.stopSession();
      } catch (e) {
        console.log('Error stopping session during dispose:', e);
      }
    }

    // Dispose all managers - each in its own try-catch to ensure all get attempted.
    // @jupyterlab/services managers have internal Poll objects that throw async
    // "Poll is disposed" errors during teardown (especially during HMR). These
    // are harmless — suppress them with a one-shot unhandledrejection handler.
    const suppressPollErrors = (e: PromiseRejectionEvent) => {
      if (e.reason?.message?.includes('Poll') && e.reason?.message?.includes('disposed')) {
        e.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', suppressPollErrors);
    // Remove handler after a tick so in-flight poll rejections are caught
    setTimeout(() => window.removeEventListener('unhandledrejection', suppressPollErrors), 500);

    try {
      if (this.sessionManager && !this.sessionManager.isDisposed) {
        this.sessionManager.dispose();
      }
    } catch (e) {
      console.log('Error disposing session manager:', e);
    }

    try {
      if (this.contentsManager && !this.contentsManager.isDisposed) {
        this.contentsManager.dispose();
      }
    } catch (e) {
      console.log('Error disposing contents manager:', e);
    }

    try {
      if (this.kernelManager && !this.kernelManager.isDisposed) {
        this.kernelManager.dispose();
      }
    } catch (e) {
      console.log('Error disposing kernel manager:', e);
    }

    try {
      if (this.terminalManager && !this.terminalManager.isDisposed) {
        this.terminalManager.dispose();
      }
    } catch (e) {
      console.log('Error disposing terminal manager:', e);
    }
  }
}

// singleton instance management
let instances: Map<string, NotebookService> = new Map();

export function getNotebookService(config: NotebookConfig): NotebookService {
  const key = config.sessionId;
  const existing = instances.get(key);

  // If existing service is disposed, remove it and create a new one
  if (existing && existing.isDisposed) {
    instances.delete(key);
  }

  if (!instances.has(key)) {
    instances.set(key, new NotebookService(config));
  }

  return instances.get(key)!;
}

export async function disposeNotebookService(sessionId: string): Promise<void> {
  const service = instances.get(sessionId);
  if (service && !service.isDisposed) {
    await service.dispose();
  }
  instances.delete(sessionId);
}

export default NotebookService;